/**
 * EL PESO DE REFERENCIA SEMANAL (regla simplificada, Enrique, 29-10).
 *
 * El primer peso de la semana (de lunes a domingo) es el de referencia, sin preguntar. Si al
 * apuntar uno ya había otros esa semana, se pregunta cuál dejar, con el de ahora marcado:
 *
 *     TU PESO SEMANAL
 *     Seleccionar peso de referencia semanal
 *     Hoy, miércoles 87,5 kg · Martes 87,9 kg · Lunes 88,4 kg
 *     [Confirmar]
 *
 *     TU PESO SEMANAL  87,9 kg  Peso del martes          (`PesoSemanalElegido`)
 *     [Cambiar]   -> la misma lista (solo si la semana tiene más de un peso)
 *
 * Qué se pregunta y qué vale lo decide el servidor (`GET /clients/me/peso-semanal`,
 * `core/peso_de_referencia`): aquí solo se pinta `estado` y se manda la fecha elegida.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { kg } from '../lib/pesoValido';
import { hoyLocal } from '../lib/horaEspana';

const ORANGE = '#FF671F';

/** «Peso del martes», «Peso de hoy». */
const delDia = (p) => (p.dia.startsWith('Hoy') ? 'Peso de hoy' : `Peso del ${p.dia.toLowerCase()}`);

export const PesoSemanalElegido = ({ elegido, onCambiar }) => (
    <div className="rounded-2xl border p-4 text-center" data-testid="peso-semanal-elegido"
        style={{ borderColor: `${ORANGE}55`, backgroundColor: `${ORANGE}0D` }}>
        <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Tu peso semanal</p>
        <p className="text-2xl font-bold tabular-nums mt-1" style={{ color: ORANGE }}>{kg(elegido.valor)} kg</p>
        <p className="text-sm text-muted-foreground">{delDia(elegido)}</p>
        {onCambiar && (
            <button type="button" onClick={onCambiar} data-testid="peso-semanal-cambiar"
                className="mt-3 w-full rounded-xl py-3 text-sm font-bold border border-border bg-card text-foreground hover:border-foreground/30">
                Cambiar
            </button>
        )}
    </div>
);

/** `cambiando`: abre directamente la lista, con el que ya eligió marcado («Cambiar»), y con
 *  «Cancelar» (`onCancelar`) para salir sin tocar nada. */
const PesoDeReferencia = ({ api, estado, onElegido, cambiando = false, onCancelar }) => {
    const [marcado, setMarcado] = useState(estado?.propuesto?.fecha || estado?.elegido?.fecha || null);
    const [guardando, setGuardando] = useState(false);
    if (!estado?.ofrecer && !cambiando) return null;

    const elegir = async (fecha) => {
        setGuardando(true);
        try {
            const r = await api.post('/clients/me/peso-semanal', { fecha, hoy: hoyLocal() });
            onElegido?.(r.data);
        } catch (e) {
            console.error('No se pudo guardar el peso de referencia semanal', e);
            toast.error('No hemos podido guardar tu peso semanal. Inténtalo en un momento.');
        } finally {
            setGuardando(false);
        }
    };

    return (
            <div className="rounded-2xl border border-border bg-card p-4 space-y-3" data-testid="peso-referencia-modificar">
                <div>
                    <p className="text-base font-bold text-foreground uppercase"
                        style={{ fontFamily: 'Barlow Condensed', letterSpacing: '0.02em' }}>Tu peso semanal</p>
                    <p className="text-sm text-muted-foreground">Seleccionar peso de referencia semanal</p>
                </div>
                <div className="space-y-2">
                    {estado.pesos.map((p) => {
                        const activo = p.fecha === marcado;
                        return (
                            <button key={p.fecha} type="button" onClick={() => setMarcado(p.fecha)}
                                aria-pressed={activo} data-testid={`peso-referencia-opcion-${p.fecha}`}
                                className={`w-full flex items-center justify-between rounded-xl border px-3 py-3 text-sm transition-colors ${
                                    activo ? 'bg-brand/10' : 'border-border bg-muted hover:border-foreground/30'}`}
                                style={activo ? { borderColor: ORANGE } : undefined}>
                                <span className="text-foreground/80">{p.dia}</span>
                                <span className="font-bold text-foreground tabular-nums">{kg(p.valor)} kg</span>
                            </button>
                        );
                    })}
                </div>
                <button type="button" onClick={() => elegir(marcado)} disabled={guardando || !marcado}
                    data-testid="peso-referencia-confirmar-otro"
                    className="w-full rounded-xl py-3 text-sm font-bold text-white uppercase tracking-wider disabled:opacity-50"
                    style={{ backgroundColor: ORANGE }}>
                    {guardando ? 'Guardando…' : 'Confirmar'}
                </button>
                {cambiando && onCancelar && (
                    <button type="button" onClick={onCancelar} disabled={guardando}
                        data-testid="peso-referencia-cancelar"
                        className="w-full py-2 text-sm font-semibold text-muted-foreground hover:text-foreground">
                        Cancelar
                    </button>
                )}
            </div>
    );
};

export default PesoDeReferencia;
